import { Component, Inject, OnInit } from '@angular/core';
import {
    CollectionHeader,
    MODAL_DATA,
    NgbActiveModal,
    NotificationService,
    SortDirection
} from '@agilent/ui-element';
import { SoftwareImagesApi } from '@app/api/software-images-api.service';
import { InfoDialogComponent } from '@app/shared/ui/info-dialog.component';
import { AcSoftwareId } from '@app/pages/acs/ac-details/ac-software/ac-software-page.service';

export enum FromPage {
    CID_SOFTWARE_PAGE,
    SERVER_SOFTWARE_PAGE,
}

@Component({
    selector: 'app-image-details-dialog',
    templateUrl: './software-image-selection-dialog.component.html',
    styleUrls: ['./software-image-selection-dialog.component.scss']
})
export class SoftwareImageSelectionDialogComponent implements OnInit {

    private dialogTitle: string;
    private softwareId: number;
    private softwareImageVersions: any[];
    private originalVersionId = 1;
    private selectedVersionId = 1;
    private isRequired = true;
    private onSave = null;
    public isSaving = false;
    private softwareImageHeaders: CollectionHeader<any>[] = [
        { key: 'radio_btn', label: '', sort: false },
        { key: 'name', label: 'Version', sort: { direction: SortDirection.NONE } },
        { key: 'releaseDate', label: 'Release Date', sort: { direction: SortDirection.NONE } },
        { key: 'release_notes', label: 'Release Notes', sort: false }
    ];
    isAnyUpdateSelected: boolean;
    selectedVersion: any;
    private fromPage: FromPage;
    private name: string;

    constructor(
        private activeModal: NgbActiveModal,
        @Inject(MODAL_DATA) private modalData: any,
        private softwareImagesApiService: SoftwareImagesApi,
        private notificationService: NotificationService
    ) {
        this.dialogTitle = modalData.title;
        this.softwareId = modalData.softwareId;
        this.originalVersionId = modalData.selectedVersionId || null;
        this.selectedVersionId = this.originalVersionId;
        this.isRequired = modalData.isRequired;
        this.onSave = modalData.onSave;
        this.isAnyUpdateSelected = modalData.isAnyUpdateSelected;
        this.fromPage = modalData.fromPage;
        this.name = modalData.name;

        // Create the software image versions list, including the
        // Not Installed option if the software is not required
        this.softwareImageVersions = [];
        if (!this.isRequired) {
            this.softwareImageVersions.push({
                id: null,
                name: 'Not Installed',
                releaseDate: null,
                release_notes: null,
                isDisabled: false
            });
        }
        for (const item of modalData.softwareImageVersions) {
            if (item.isVisible || item.id === modalData.selectedVersionId) {
                if (item.testError) { // TODO this probably isn't the best place to do this
                    item.name = item.name + " (" + item.testError + ")";
                }
                this.softwareImageVersions.push(item);
            }
        }

        if (modalData.shouldBeUninstalled) {
            this.selectedVersionId = this.originalVersionId = null;
        }
    }

    ngOnInit() {
        //
    }

    isSaveDisabled() {
        return this.isSaving || (this.selectedVersionId === this.originalVersionId);
    }

    async select() {
        const result = { selectedVersionId: this.selectedVersionId };
        if (this.onSave) {
            try {
                this.isSaving = true;
                await this.onSave(result);
            }
            catch (ex) {
                console.error(ex);
            }
            finally {
                this.isSaving = false;
            }
        }
        this.activeModal.close(result);
    }

    cancel() {
        this.activeModal.dismiss();
    }

    async openReleaseNotes($event, versionId) {
        $event.stopPropagation();
        try {
            const resp = await this.softwareImagesApiService
                .getSoftwareVersionReleaseNotesUrl(this.softwareId, versionId);

            window.open(resp.url);
        }
        catch (ex) {
            this.notificationService.error('Release notes not found');
        }
    }

    async handleClick(selectedRow) {
        const selectedItem = selectedRow.data;
        this.selectedVersion = selectedItem;
        if (selectedItem && selectedItem.isDisabled) {
            await this.notificationService.showModal(InfoDialogComponent, {
                content: `To downgrade to version <b>${selectedItem.name}</b>` +
                    ', you need to reset your OpenLab CDS software first. \n' +
                    'Please go to the <b>Administration</b> page where you' +
                    ' can perform this action.',
                title: 'Information',
                confirmButtonText: 'OK'
            });
        } else {
            this.selectedVersionId = selectedItem.id;
        }
    }

    async showWarningDialog() {
        // skip warning dialog if changing linux or windows update version
        const skipWarning = this.softwareId == AcSoftwareId.AC || this.softwareId == AcSoftwareId.WINDOWS_UPDATE || this.softwareId >= AcSoftwareId.ADD_ON;
        if (skipWarning) {
            return await this.select();
        }

        // get currently used version name
        let originalVersionName = '';
        for (let version of this.softwareImageVersions) {
            if (version.id == this.originalVersionId) {
                originalVersionName = version.name;
                break;
            }
        }

        // generate warning dialog's content and title
        let content_title =  '';
        let content_text = '';
        if (this.softwareId == AcSoftwareId.AIC)
        {
            content_title = 'Are you sure you want to change CDS version?'
            if (this.fromPage === FromPage.CID_SOFTWARE_PAGE) {
                content_text = `After changing this CID to \'${this.selectedVersion.name}\':` + 
                    `<ul><li>Only CDS clients running \'${this.selectedVersion.name}\' must be used</li>` +
                    `<li>Drivers will revert to original revisions. Required driver changes must also be made prior to use.</li></ul>`;
            } else if (this.fromPage === FromPage.SERVER_SOFTWARE_PAGE) {
                content_text = `After changing CID/s to \'${this.selectedVersion.name}\':` + 
                    `<ul><li>Only CDS clients running \'${this.selectedVersion.name}\' must be used</li>` +
                    `<li>Drivers will revert to original revisions. Required driver changes must also be made prior to use.</li></ul>`;
            }
        }
        else // drivers
        {
            if (this.originalVersionId == null) // if installing the driver
            {
                return await this.select();
            } else if (this.selectedVersionId == null) // if removing the driver
            {
                content_title = `Are you sure you want to remove the driver?`;
                if (this.fromPage === FromPage.CID_SOFTWARE_PAGE) {
                    content_text = `After removing \'${originalVersionName}\' from this CID, note that it cannot be used to control ${this.name}. Are you sure you want to uninstall this driver?`;
                } else if (this.fromPage === FromPage.SERVER_SOFTWARE_PAGE) {
                    content_text = `After removing \'${originalVersionName}\' from CID/s, note that they cannot be used to control ${this.name}. Are you sure you want to uninstall this driver?`;
                }
            } else // if changing the driver version
            {
                content_title = `Are you sure you want to change the driver?`;
                if (this.fromPage === FromPage.CID_SOFTWARE_PAGE) {
                    content_text = `After installing \'${this.selectedVersion.name}\' on this CID, note that it can be used only from CDS client systems running the exact same version of this driver. Are you sure you want to make this change now?`;
                } else if (this.fromPage === FromPage.SERVER_SOFTWARE_PAGE) {
                    content_text = `After installing \'${this.selectedVersion.name}\' on CID/s, note that they can be used only from CDS client systems running the exact same version of this driver. Are you sure you want to make this change now?`;
                }
            }
        }

        const modelRef = this.notificationService.showModal(InfoDialogComponent,
            {
                content: content_text,
                title: content_title,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No',
                isWarningDialog: false
            });

        modelRef.result.then(() => {
            return this.select();
        }).catch(reason => {
            // Tell the user an error occurred
            if (reason) {
                this.notificationService.error(reason);
            }
            this.cancel();
        });
    }
}
